import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Make.com Webhook URL
      const webhookURL = 'https://hook.eu1.make.com/am3xm9i4zczp4pu50a3204d55f2onmwa';
      
      // Prepare data for webhook
      const webhookData = {
        full_name: formData.fullName,
        phone_number: formData.phone,
        email: formData.email,
        service_requested: formData.service,
        message_content: formData.message,
        timestamp: new Date().toISOString(),
        source: 'Deep Control Website'
      };
      
      // Send to Make.com webhook
      const response = await fetch(webhookURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });
      
      if (response.ok) {
        // Show success message
        toast({
          title: "Message Sent Successfully!",
          description: "We'll get back to you within 1 hour. Thank you for contacting Deep Control.",
        });
        
        // Clear form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        throw new Error('Webhook failed');
      }
    } catch (error) {
      // Show error message
      toast({
        title: "Something went wrong",
        description: "Please try again or call us directly at 020 8059 9941.",
        variant: "destructive"
      });
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Contact Deep Control HVAC"
        description="Get in touch with Deep Control for air conditioning installation, repair and 24/7 emergency support. Call 020 8059 9941, WhatsApp, or email info@deepcontrol.co.uk."
        path="/contact"
      />
      {/* Hero Section */}
      <section 
        className="relative min-h-[50vh] flex items-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(10, 14, 20, 0.55), rgba(10, 14, 20, 0.70)), url('/images/hero-contact.jpg')`,
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <span className="inline-block text-xs bg-white/10 px-4 py-2 rounded-full text-[#FF8C00] font-bold border border-[#FF8C00]/30 tracking-wider mb-6">
            CONTACT US
          </span>
          <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-6">
            Get In Touch <br />With <span className="text-[#FF8C00]">Our Team</span>
          </h1>
          <p className="text-gray-300 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto">
            Have a question or need a quote? Our team is here to help. Get in touch today and we'll respond as quickly as possible.
          </p>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-20 bg-[#0A0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#121820] border border-white/5 rounded-2xl p-8">
                <h2 className="text-3xl font-bold mb-6">Send Us A Message</h2>
                <p className="text-gray-400 mb-8">Fill out the form below, and we'll get back to you shortly.</p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="fullName" className="text-white mb-2 block">Full Name</Label>
                      <Input
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="John Smith"
                        required
                        className="bg-[#0A0E14] border-white/10 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-white mb-2 block">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="020 1234 5678"
                        required
                        className="bg-[#0A0E14] border-white/10 text-white placeholder:text-gray-500"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-white mb-2 block">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="bg-[#0A0E14] border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="service" className="text-white mb-2 block">Service Required</Label>
                    <Select onValueChange={(value) => setFormData({...formData, service: value})}>
                      <SelectTrigger id="service" className="bg-[#0A0E14] border-white/10 text-white">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121820] border-white/10 text-white">
                        <SelectItem value="installation">Air Conditioning Installation</SelectItem>
                        <SelectItem value="repairs">Repairs & Breakdowns</SelectItem>
                        <SelectItem value="servicing">Servicing & Maintenance</SelectItem>
                        <SelectItem value="commercial">Commercial HVAC</SelectItem>
                        <SelectItem value="callout">24/7 Call Out</SelectItem>
                        <SelectItem value="other">Other / General Enquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-white mb-2 block">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="How can we help you?"
                      rows={6}
                      required
                      className="bg-[#0A0E14] border-white/10 text-white placeholder:text-gray-500 resize-none"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-[#FF8C00] hover:bg-[#FF8C00]/90 text-white font-bold py-6 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send size={20} className="mr-2" /> 
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-6">
              {/* Why Contact Deep Control */}
              <div className="bg-[#121820] border border-white/5 rounded-2xl p-6">
                <h3 className="text-xl font-bold mb-6">Why Contact Deep Control?</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#FF8C00]/10 flex items-center justify-center">
                      <Clock size={20} className="text-[#FF8C00]" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Fast Response</p>
                      <p className="text-sm text-gray-400">We aim to respond to all enquiries within 1 hour during working hours.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#FF8C00]/10 flex items-center justify-center">
                      <Phone size={20} className="text-[#FF8C00]" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Expert Support</p>
                      <p className="text-sm text-gray-400">Speak directly with our HVAC specialists for expert advice.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#FF8C00]/10 flex items-center justify-center">
                      <Mail size={20} className="text-[#FF8C00]" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Transparent Quotes</p>
                      <p className="text-sm text-gray-400">We provide clear, competitive and transparent quotations.</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Direct Contact */}
              <div className="bg-[#121820] border border-white/5 rounded-2xl p-6">
                <h3 className="text-xl font-bold mb-6">Contact Info</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Phone size={20} className="text-[#FF8C00] mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Call Us</p>
                      <a href="tel:02080599941" className="font-bold text-lg hover:text-[#FF8C00] transition-colors">
                        020 8059 9941
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MessageCircle size={20} className="text-[#FF8C00] mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-400 mb-1">WhatsApp Us</p>
                      <a href="https://wa.me/447990547352" target="_blank" rel="noopener noreferrer" className="font-bold hover:text-[#FF8C00] transition-colors">
                        020 8059 9941
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail size={20} className="text-[#FF8C00] mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Email Us</p>
                      <a href="mailto:info@deepcontrol.co.uk" className="font-bold hover:text-[#FF8C00] transition-colors break-all">
                        info@deepcontrol.co.uk
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock size={20} className="text-[#FF8C00] mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Office Hours</p>
                      <p className="font-semibold">Mon - Sat: 8:00am - 6:00pm</p>
                      <p className="text-sm text-gray-400">Sunday: Emergency Only</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Need Urgent Assistance */}
              <div className="bg-gradient-to-br from-[#FF8C00]/20 to-[#FF8C00]/5 border border-[#FF8C00]/30 rounded-2xl p-6">
                <h3 className="text-xl font-bold mb-3">Need Urgent Assistance?</h3>
                <p className="text-sm text-gray-300 mb-4">
                  We offer 24/7 emergency call-outs for breakdowns and urgent repairs.
                </p>
                <a href="tel:02080599941">
                  <Button className="w-full bg-[#FF8C00] hover:bg-[#FF8C00]/90 text-white font-bold py-4" data-testid="contact-urgent-call">
                    <Phone size={18} className="mr-2" /> Call Now: 020 8059 9941
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Direct Section */}
      <section className="py-20 bg-[#121820]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MessageCircle size={60} className="text-[#FF8C00] mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-4">WhatsApp Us Directly</h2>
          <p className="text-gray-400 text-lg mb-8">
            Chat with our team instantly on WhatsApp for quick support and enquiries.
          </p>
          <a href="https://wa.me/447990547352" target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#25D366] hover:bg-[#25D366]/90 text-white font-bold px-10 py-6 rounded-md text-base">
              <MessageCircle size={20} className="mr-2" /> Chat on WhatsApp
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
